// src/pages/LandingAds/LandingAds.js
import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import {
  Sparkles,
  ShieldCheck,
  Headset,
  Timer,
  ArrowLeft,
  MapPin,
  Plane,
  Hotel,
  ChevronLeft,
  ChevronRight,
  Users,
  ReceiptText,
  BadgeCheck,
  CalendarDays,
  CheckCircle2,
  HelpCircle,
  MessageCircle,
  PhoneCall,
  Lock,
  ChevronDown,
  FileCheck2,
} from "lucide-react";

import LeadCardForm from "../../components/LeadCardForm/LeadCardForm";
import "./LandingAds.css";
import CustSaying from "../../components/CustSaying/CustSaying";
import crImg from "../../assets/image/سجل1.png";
import tourismImg from "../../assets/image/tourism-license.png";
import indonesiaImg from "../../assets/image/اندونسيا.jpeg";
import azerbaijanImg from "../../assets/image/أذربيجان.jpeg";
import georgiaImg from "../../assets/image/georgia.jpeg";
import thailandImg from "../../assets/image/thailand.jpeg";
import turkeyImg from "../../assets/image/turkiye.jpeg";
import bosniaImg from "../../assets/image/bosnia.jpeg";
import dubaiImg from "../../assets/image/dubai.jpeg";
import mauritiusImg from "../../assets/image/mauritius.jpeg";
import malaysiaImg from "../../assets/image/malaysia.png";
import srilankaImg from "../../assets/image/srilanka.jpeg";
import europeImg from "../../assets/image/europe.png";

export default function LandingAds() {
  const prefersReducedMotion = useReducedMotion();

  const formRef = useRef(null);
  const servicesRef = useRef(null);
  const destinationsRef = useRef(null);
  const whyRef = useRef(null);
  const programRef = useRef(null);
  const faqRef = useRef(null);
  const licensesRef = useRef(null);
  const finalCtaRef = useRef(null);

  const scrollTo = (ref) => {
    if (!ref?.current) return;
    ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ===================== performance helpers =====================
  // ✅ حوّل imgur.com/xxxx.jpg -> i.imgur.com/xxxx.jpg (تحميل مباشر)
  const toDirectImgur = useCallback((url) => {
    if (!url) return url;
    // لو الرابط أصلاً i.imgur.com سيبه
    if (url.includes("i.imgur.com")) return url;

    // لو imgur.com/abc.jpg أو imgur.com/abc
    // نخليه i.imgur.com/abc.jpg (ولو مفيش امتداد نزود .jpg)
    const m = url.match(/imgur\.com\/([^.?/]+)(\.(jpg|jpeg|png|webp|avif))?/i);
    if (!m) return url;

    const id = m[1];
    const ext = m[2] || ".jpg";
    return `https://i.imgur.com/${id}${ext}`;
  }, []);

  // ===================== content =====================
  const trust = useMemo(
    () => [
      { id: 1, label: "جهة موثوقة", Icon: ShieldCheck },
      { id: 2, label: "دعم باللغة العربية", Icon: Headset },
      { id: 3, label: "استجابة سريعة", Icon: Timer },
    ],
    [],
  );

  const quickCards = useMemo(
    () => [
      {
        id: 1,
        title: "برنامج مُصمّم حسب احتياجك",
        desc: "نُعدّ لك مسار الرحلة والأنشطة بما يتوافق مع ميزانيتك وتفضيلاتك.",
        Icon: Sparkles,
      },
      {
        id: 2,
        title: "خيارات طيران وفنادق",
        desc: "نقارن البدائل ونقدّم أفضل الخيارات المتاحة وقت الحجز.",
        Icon: Plane,
      },
      {
        id: 3,
        title: "متابعة عربية شاملة",
        desc: "نرافقك قبل السفر وأثناءه وبعده لضمان تجربة سلسة.",
        Icon: BadgeCheck,
      },
    ],
    [],
  );

  const services = useMemo(
    () => [
      {
        id: 1,
        title: "تنظيم الرحلات",
        desc: "تنسيق رحلة متكاملة دون عناء التفاصيل.",
        Icon: MapPin,
      },
      {
        id: 2,
        title: "حجز الفنادق",
        desc: "ترشيحات دقيقة وفق الميزانية والموقع وتقييمات النزلاء.",
        Icon: Hotel,
      },
      {
        id: 3,
        title: "حجز تذاكر الطيران",
        desc: "اقتراح مسارات وأسعار مناسبة حسب تفضيلاتك.",
        Icon: Plane,
      },
      {
        id: 4,
        title: "دعم العملاء",
        desc: "مساندة مستمرة وإجابات واضحة قبل وأثناء وبعد الرحلة.",
        Icon: Headset,
      },
      {
        id: 5,
        title: "موثوقية والتزام",
        desc: "إجراءات منظمة وتواصل احترافي من البداية حتى العودة.",
        Icon: ShieldCheck,
      },
      {
        id: 6,
        title: "شفافية كاملة",
        desc: "عرض واضح وتفاصيل دقيقة دون رسوم مخفية.",
        Icon: ReceiptText,
      },
    ],
    [],
  );

  const destinations = useMemo(
    () => [
      { id: 1, name: "إندونيسيا", slug: "indonesia", image: indonesiaImg },
      { id: 2, name: "أذربيجان", slug: "azerbaijan", image: azerbaijanImg },
      { id: 3, name: "جورجيا", slug: "georgia", image: georgiaImg },
      { id: 4, name: "تايلاند", slug: "thailand", image: thailandImg },
      { id: 5, name: "تركيا", slug: "turkiye", image: turkeyImg },
      { id: 6, name: "البوسنة", slug: "bosnia", image: bosniaImg },
      { id: 7, name: "دبي", slug: "dubai", image: dubaiImg },
      { id: 9, name: "موريشيوس", slug: "mauritius", image: mauritiusImg },
      { id: 10, name: "ماليزيا", slug: "malaysia", image: malaysiaImg },
      { id: 11, name: "سريلانكا", slug: "srilanka", image: srilankaImg },
      { id: 14, name: "جولات أوروبا", slug: "europe", image: europeImg },
    ],
    [],
  );

  const whyItems = useMemo(
    () => [
      {
        id: 1,
        title: "برامج متكاملة",
        desc: "طيران + فندق + تنقلات (حسب الطلب).",
        Icon: Plane,
      },
      {
        id: 2,
        title: "مناسب للعائلات",
        desc: "خيارات مريحة وآمنة تناسب الجميع.",
        Icon: Users,
      },
      {
        id: 3,
        title: "دعم على مدار الساعة",
        desc: "متابعة عربية قبل وأثناء وبعد السفر.",
        Icon: Headset,
      },
      {
        id: 4,
        title: "أسعار واضحة",
        desc: "تفاصيل شفافة دون مفاجآت.",
        Icon: ReceiptText,
      },
      {
        id: 5,
        title: "تنسيق احترافي",
        desc: "مسار وأنشطة وفق الميزانية والاهتمامات.",
        Icon: BadgeCheck,
      },
    ],
    [],
  );

  const sampleProgram = useMemo(
    () => [
      {
        id: 1,
        day: "اليوم 1",
        text: "الوصول — الاستقبال من المطار — تسجيل الدخول للفندق.",
      },
      { id: 2, day: "اليوم 2", text: "جولة مدينة وزيارة أبرز المعالم." },
      {
        id: 3,
        day: "اليوم 3",
        text: "أنشطة طبيعة/جبال + خيارات إضافية حسب الرغبة.",
      },
      { id: 4, day: "اليوم 4", text: "تنقلات منظمة + تجربة محلية + تسوق." },
      { id: 5, day: "اليوم 5", text: "رحلة يوم كامل + برنامج حسب الوجهة." },
      { id: 6, day: "اليوم 6", text: "يوم حر مع اقتراحات ملائمة لاهتماماتك." },
      {
        id: 7,
        day: "اليوم 7",
        text: "تسجيل الخروج — التوصيل إلى المطار — العودة.",
      },
    ],
    [],
  );

  const faqs = useMemo(
    () => [
      {
        id: 1,
        q: "هل يشمل العرض تذاكر الطيران؟",
        a: "يمكن تضمين الطيران حسب الميزانية ونوع العرض، ونقدّم عادةً أكثر من خيار (مباشر/ترانزيت) وفق المتاح.",
      },
      {
        id: 2,
        q: "هل البرامج مناسبة للعائلات؟",
        a: "نعم، نقترح خيارات إقامة مناسبة للعائلات ومسارات تراعي الراحة واحتياجات الأطفال.",
      },
      {
        id: 3,
        q: "هل يتوفر مرشد عربي؟",
        a: "يتوفر حسب الوجهة، ويمكن ترتيب مرشد أو سائق عربي عند الحاجة.",
      },
      {
        id: 4,
        q: "هل يمكن تعديل البرنامج؟",
        a: "بالطبع، نعدّل البرنامج حسب رغبتك قبل التأكيد النهائي.",
      },
      {
        id: 5,
        q: "كيف يتم التعامل مع البيانات؟",
        a: "تُستخدم بياناتك لإعداد العرض فقط، مع الالتزام بالخصوصية وعدم الإزعاج.",
      },
    ],
    [],
  );

  const licenses = useMemo(
    () => [
      {
        id: 1,
        title: "السجل التجاري",
        desc: "نعمل بشكل رسمي ومنظم، مع بيانات واضحة تعزز الثقة وتضمن شفافية التعامل من أول خطوة حتى إتمام الحجز.",
        img: crImg,
        Icon: FileCheck2,
      },
      {
        id: 2,
        title: "رخصة السياحة",
        desc: "مرخّصون لمزاولة النشاط السياحي، بما يضمن تقديم خدمة احترافية وفق إجراءات معتمدة والتزام كامل بمعايير التنظيم.",
        img: tourismImg,
        Icon: BadgeCheck,
      },
    ],
    [],
  );

  // ===================== animations =====================
  const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: prefersReducedMotion ? 0 : 0.55 },
    },
  };

  const stagger = {
    hidden: {},
    show: { transition: { staggerChildren: prefersReducedMotion ? 0 : 0.08 } },
  };

  // ===================== destinations slider =====================
  const destSliderRef = useRef(null);
  const scrollDestBy = (dir = 1) => {
    const el = destSliderRef.current;
    if (!el) return;
    const card = el.querySelector(".destCard");
    const w = card ? card.getBoundingClientRect().width : 260;
    el.scrollBy({ left: dir * (w + 16), behavior: "smooth" });
  };

  // subtle background shift
  const [bgShift, setBgShift] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || 0;
      setBgShift(Math.min(16, y * 0.012));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ===================== FAQ accordion =====================
  const [openFaq, setOpenFaq] = useState(null);

  const onWhatsApp = () => {
    const phone = "+966559163952";
    window.open(`https://wa.me/${phone}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="lpRoot" dir="rtl">
      {/* ✅ Performance hints */}
      <Helmet>
        {/* Imgur direct host */}
        <link rel="preconnect" href="https://i.imgur.com" crossOrigin="" />
        <link rel="dns-prefetch" href="https://i.imgur.com" />
      </Helmet>

      {/* ===================== TOP ===================== */}
      <section className="lpTop" style={{ "--bgShift": `${bgShift}px` }}>
        <div className="lpTopBg" aria-hidden="true" />
        <div className="lpTopNoise" aria-hidden="true" />

        <div className="lpContainer">
          <div className="lpTopGrid">
            {/* Content */}
            <motion.div
              className="lpTopContent"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.35 }}
              variants={stagger}
            >
              <motion.div className="lpBadge" variants={fadeUp}>
                <Sparkles size={16} />
                <span>خصم خاص للسعوديين على برامج مختارة</span>
              </motion.div>

              <motion.h1 className="lpTitle" variants={fadeUp}>
                دعنا نُجهّز لك <span className="gradText">برنامج سفر</span>{" "}
                يناسبك
              </motion.h1>

              <motion.p className="lpSubtitle" variants={fadeUp}>
                املئ النموذج (رقم الجوال + المدينة + الوجهة)، وخلال وقت قصير
                نرسل لك عرضًا مناسبًا، مع متابعة باللغة العربية حتى بعد إتمام
                الحجز.
              </motion.p>

              <motion.div className="lpTopActions" variants={fadeUp}>
                <button
                  type="button"
                  className="lpGhostBtn"
                  onClick={() => scrollTo(destinationsRef)}
                >
                  تصفّح الوجهات <ArrowLeft size={18} />
                </button>
                <button
                  type="button"
                  className="lpGhostBtn"
                  onClick={() => scrollTo(whyRef)}
                >
                  لماذا نحن <ArrowLeft size={18} />
                </button>
                <button
                  type="button"
                  className="lpGhostBtn"
                  onClick={() => scrollTo(licensesRef)}
                >
                  اعتماداتنا <ArrowLeft size={18} />
                </button>
                <button
                  type="button"
                  className="lpGhostBtn"
                  onClick={() => scrollTo(faqRef)}
                >
                  الأسئلة الشائعة <ArrowLeft size={18} />
                </button>
              </motion.div>

              <motion.div className="lpTrustRow" variants={fadeUp}>
                {trust.map(({ id, label, Icon }) => (
                  <div key={id} className="lpTrustPill">
                    <Icon size={18} />
                    <span>{label}</span>
                  </div>
                ))}
              </motion.div>

              <motion.div className="lpQuickGrid" variants={stagger}>
                {quickCards.map(({ id, title, desc, Icon }) => (
                  <motion.div
                    key={id}
                    className="lpQuickCard"
                    variants={fadeUp}
                    whileHover={prefersReducedMotion ? undefined : { y: -4 }}
                    transition={{ type: "spring", stiffness: 260, damping: 18 }}
                  >
                    <div className="qIcon">
                      <Icon size={20} />
                    </div>
                    <div className="qBody">
                      <div className="qTitle">{title}</div>
                      <div className="qDesc">{desc}</div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* FORM */}
            <motion.div
              className="lpTopForm"
              ref={formRef}
              initial={{ opacity: 0, y: 18, scale: 0.99 }}
              whileInView={{
                opacity: 1,
                y: 0,
                scale: 1,
                transition: { duration: prefersReducedMotion ? 0 : 0.6 },
              }}
              viewport={{ once: true, amount: 0.35 }}
            >
              <div className="lpFormShell">
                <div className="lpFormHead">
                  <div className="lpFormHeadTitle">احصل على برنامجك الآن</div>
                </div>

                <LeadCardForm
                  showHelmet={false}
                  variant="embed"
                  className="lpFormEmbed"
                  successPath="/success"
                  apiUrl="https://new-tnzeem-back-av7ic.ondigitalocean.app/crm/create-customer-wordpress/"
                  countryCode="SA"
                  title="التنظيم للسفر والسياحة"
                />

                <div className="lpFormFoot">
                  <div className="lpFootItem">
                    <Lock size={16} /> خصوصية البيانات
                  </div>
                  <div className="lpFootItem">
                    <Timer size={16} /> استجابة سريعة
                  </div>
                  <div className="lpFootItem">
                    <Headset size={16} /> متابعة عربية
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===================== DESTINATIONS ===================== */}
      <section className="lpDestinationsRef" ref={destinationsRef}>
        <div className="lpContainer">
          <div className="lpDestTop">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.35 }}
              variants={stagger}
            >
              <motion.h2 className="lpH2" variants={fadeUp}>
                وجهات نسافر إليها
              </motion.h2>
              <motion.p className="lpP" variants={fadeUp}>
                اختار الوجهة، وسنُعدّ لك عرضًا مناسبًا خلال وقت قصير.
              </motion.p>
            </motion.div>

            <div className="lpDestNav">
              <button
                type="button"
                className="lpNavBtn"
                onClick={() => scrollDestBy(-1)}
                aria-label="prev"
              >
                <ChevronRight />
              </button>
              <button
                type="button"
                className="lpNavBtn"
                onClick={() => scrollDestBy(1)}
                aria-label="next"
              >
                <ChevronLeft />
              </button>
            </div>
          </div>

          <div
            className="destSlider"
            ref={destSliderRef}
            role="region"
            aria-label="destinations slider"
          >
            {destinations.map((d, idx) => {
  const directSrc = d.image;

  return (
    <motion.button
      type="button"
      key={d.id}
      className="destCard"
      initial={{ opacity: 0, y: 14 }}
      whileInView={{
        opacity: 1,
        y: 0,
        transition: {
          duration: prefersReducedMotion ? 0 : 0.45,
          delay: prefersReducedMotion ? 0 : idx * 0.02,
        },
      }}
      viewport={{ once: true, amount: 0.35 }}
      onClick={() => scrollTo(formRef)}
    >
      <img
        className="destImg"
        src={directSrc}
        alt={d.name}
        loading="eager"
        fetchpriority="high"
        decoding="sync"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />

      <div className="destShade" />
      <div className="destOverlay">
        <div className="destName">{d.name}</div>
        <div className="destBtn">
          اطلب برنامجًا للوجهة <ArrowLeft size={18} />
        </div>
      </div>
    </motion.button>
  );
})}

          </div>

          <div className="lpDestFooterNote">
            لدينا وجهات عديدة — إن لم تجد وجهتك هنا، اكتبها في النموذج.
          </div>
        </div>
      </section>

      {/* ===================== WHY CHOOSE US ===================== */}
      <section className="lpWhy" ref={whyRef}>
        <div className="lpContainer">
          <motion.div
            className="lpHeadCenter"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
            variants={stagger}
          >
            <motion.div className="lpKicker" variants={fadeUp}>
              <BadgeCheck size={16} />
              <span>لماذا تختارنا</span>
            </motion.div>

            <motion.h2 className="lpH2" variants={fadeUp}>
              أسباب تجعل التجربة أسهل
            </motion.h2>

            <motion.p className="lpP" variants={fadeUp}>
              خدمة منظمة وتجربة سلسة — هدفنا تقديم أفضل رحلة بأقل جهد منك.
            </motion.p>
          </motion.div>

          <motion.div
            className="whyGridElegant"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            variants={stagger}
          >
            {whyItems.map(({ id, title, desc, Icon }) => (
              <motion.div
                key={id}
                className="whyCardElegant"
                variants={fadeUp}
                whileHover={prefersReducedMotion ? undefined : { y: -6 }}
              >
                <div className="whyTop">
                  <div className="whyIcon">
                    <Icon size={20} />
                  </div>
                  <div className="whyTitle">{title}</div>
                </div>
                <div className="whyDesc">{desc}</div>
              </motion.div>
            ))}
          </motion.div>

          <div className="lpWhyCta">
            <button
              type="button"
              className="lpPrimaryBtn"
              onClick={() => scrollTo(formRef)}
            >
              أرسل الطلب ودعنا نتولى التخطيط <ArrowLeft size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ===================== LICENSES ===================== */}
      <section className="lpLicenses" ref={licensesRef}>
        <div className="lpContainer">
          <motion.div
            className="lpHeadCenter"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
            variants={stagger}
          >
            <motion.div className="lpKicker" variants={fadeUp}>
              <FileCheck2 size={16} />
              <span>اعتمادات رسمية</span>
            </motion.div>
            <motion.h2 className="lpH2" variants={fadeUp}>
              سجل تجاري ورخصة سياحة
            </motion.h2>
            <motion.p className="lpP" variants={fadeUp}>
              ثقة ووضوح — نعمل بشكل رسمي ومنظم لضمان رحلة مطمئنة من البداية حتى
              النهاية.
            </motion.p>
          </motion.div>

          <div className="licensesStack">
            {licenses.map((x, idx) => {
              const Icon = x.Icon;
              return (
                <motion.div
                  key={x.id}
                  className="licenseRow"
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      duration: prefersReducedMotion ? 0 : 0.5,
                      delay: prefersReducedMotion ? 0 : idx * 0.05,
                    },
                  }}
                  viewport={{ once: true, amount: 0.3 }}
                >
                  <div className="licenseMedia">
                    <img
                      src={x.img}
                      alt={x.title}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  <div className="licenseText">
                    <div className="licenseKicker">
                      <span className="licenseIcon">
                        <Icon size={18} />
                      </span>
                      <span>اعتماد رسمي</span>
                    </div>
                    <h3 className="licenseTitle">{x.title}</h3>
                    <p className="licenseP">{x.desc}</p>

                    <div className="licenseActions">
                      <button
                        type="button"
                        className="lpPrimaryBtn isCompact"
                        onClick={() => scrollTo(formRef)}
                      >
                        تواصل معنا <ArrowLeft size={18} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===================== SAMPLE PROGRAM ===================== */}
      <section className="lpProgram" ref={programRef}>
        <div className="lpContainer">
          <div className="programGrid">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              variants={stagger}
            >
              <motion.div className="lpKicker" variants={fadeUp}>
                <CalendarDays size={16} />
                <span>نموذج برنامج</span>
              </motion.div>
              <motion.h2 className="lpH2" variants={fadeUp}>
                مثال لبرنامج 7 أيام:
              </motion.h2>
              <motion.p className="lpP" variants={fadeUp}>
                فندق — إفطار — تنقلات — جولات — استقبال من المطار (حسب الوجهة
                والعرض)
              </motion.p>

              <motion.div className="programList" variants={stagger}>
                {sampleProgram.map((x) => (
                  <motion.div
                    key={x.id}
                    className="programItem"
                    variants={fadeUp}
                  >
                    <div className="programDot">
                      <CheckCircle2 size={18} />
                    </div>
                    <div className="programText">
                      <div className="programDay">{x.day}</div>
                      <div className="programDesc">{x.text}</div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div className="programMiniNote" variants={fadeUp}>
                * اذكري الوجهة والمدينة ورقم الجوال — وسنرسل لك برنامجًا
                مناسبًا.
              </motion.div>

              <motion.div className="programBtns" variants={fadeUp}>
                <button
                  className="lpPrimaryBtn"
                  type="button"
                  onClick={() => scrollTo(formRef)}
                >
                  إرسال البيانات الآن <ArrowLeft size={18} />
                </button>
                <button
                  className="lpGhostBtn isSoft"
                  type="button"
                  onClick={() => scrollTo(destinationsRef)}
                >
                  اختيار وجهة <ArrowLeft size={18} />
                </button>
              </motion.div>
            </motion.div>

            <motion.div
              className="programCard"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{
                opacity: 1,
                y: 0,
                transition: { duration: prefersReducedMotion ? 0 : 0.55 },
              }}
              viewport={{ once: true, amount: 0.35 }}
            >
              <div className="programCardTop">
                <div className="programCardTitle">جاهزة للانطلاق؟ ✈️</div>
                <div className="programCardSub">
                  اترك التنظيم لنا… واستمتع بالتجربة
                </div>
              </div>

              <div className="programHighlights">
                <div className="ph">
                  <Plane size={18} />
                  <span>تخطيط مسار احترافي</span>
                </div>
                <div className="ph">
                  <Hotel size={18} />
                  <span>خيارات إقامة متنوعة</span>
                </div>
                <div className="ph">
                  <Headset size={18} />
                  <span>متابعة عربية</span>
                </div>
              </div>

              <div className="programCardCta">
                <button
                  type="button"
                  className="lpPrimaryBtn"
                  onClick={() => scrollTo(formRef)}
                >
                  اطلب برنامجك <ArrowLeft size={18} />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section className="lpFaq" ref={faqRef}>
        <div className="lpContainer">
          <motion.div
            className="lpHeadCenter"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
            variants={stagger}
          >
            <motion.div className="lpKicker" variants={fadeUp}>
              <HelpCircle size={16} />
              <span>FAQ</span>
            </motion.div>
            <motion.h2 className="lpH2" variants={fadeUp}>
              أسئلة شائعة
            </motion.h2>
          </motion.div>

          <div className="faqGrid">
            {faqs.map((f) => {
              const isOpen = openFaq === f.id;
              return (
                <div
                  key={f.id}
                  className={isOpen ? "faqItem isOpen" : "faqItem"}
                >
                  <button
                    type="button"
                    className="faqQ"
                    onClick={() => setOpenFaq(isOpen ? null : f.id)}
                    aria-expanded={isOpen}
                  >
                    <span className="faqQIcon">
                      <MessageCircle size={18} />
                    </span>
                    <span className="faqQText">{f.q}</span>
                    <span
                      className={isOpen ? "faqChevron isOpen" : "faqChevron"}
                    >
                      <ChevronDown size={18} />
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        className="faqA"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          duration: prefersReducedMotion ? 0 : 0.25,
                        }}
                      >
                        <div className="faqAInner">{f.a}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>

          <div className="faqCta">
            <button
              type="button"
              className="lpPrimaryBtn"
              onClick={() => scrollTo(formRef)}
            >
              إرسال الطلب الآن <ArrowLeft size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ===================== SERVICES ===================== */}
      <section className="lpServices" ref={servicesRef}>
        <div className="lpContainer">
          <motion.div
            className="lpHeadCenter"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.35 }}
            variants={stagger}
          >
            <motion.h2 className="lpH2" variants={fadeUp}>
              خدماتنا
            </motion.h2>
          </motion.div>

          <motion.div
            className="svcGrid"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            variants={stagger}
          >
            {services.map(({ id, title, desc, Icon }) => (
              <motion.div
                key={id}
                className="svcCard"
                variants={fadeUp}
                whileHover={prefersReducedMotion ? undefined : { y: -6 }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
              >
                <div className="svcIcon">
                  <Icon size={22} />
                </div>
                <div className="svcTitle">{title}</div>
                <div className="svcDesc">{desc}</div>
                <div className="svcHint" onClick={() => scrollTo(formRef)}>
                  اطلب عرضًا <ArrowLeft size={16} />
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="lpCenterCta">
            <button
              type="button"
              className="lpPrimaryBtn"
              onClick={() => scrollTo(formRef)}
            >
              إرسال الطلب ودعنا نتولى الباقي <ArrowLeft size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* testimonials existing */}
      <CustSaying />

      {/* ===================== FINAL CTA ===================== */}
      <section className="lpFinalCta" ref={finalCtaRef}>
        <div className="lpContainer">
          <div className="finalCard">
            <div className="finalLeft">
              <div className="finalTitle">هل أنت مستعد لرحلتك القادمة؟</div>
              <div className="finalSub">
                أرسل بياناتك الآن، وسنرسل لك عرضًا مناسبًا بسرعة — مع التزام
                كامل بالوضوح والخصوصية.
              </div>

              <div className="finalBadges">
                <span className="finalBadge">
                  <Timer size={16} /> استجابة سريعة
                </span>
                <span className="finalBadge">
                  <Headset size={16} /> دعم عربي
                </span>
                <span className="finalBadge">
                  <ShieldCheck size={16} /> جهة موثوقة
                </span>
              </div>
            </div>

            <div className="finalRight">
              <button
                className="lpPrimaryBtn"
                type="button"
                onClick={() => scrollTo(formRef)}
              >
                إرسال الطلب الآن <ArrowLeft size={18} />
              </button>

              <button className="finalWhats" type="button" onClick={onWhatsApp}>
                <PhoneCall size={18} />
                تواصل عبر واتساب
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FOOTER ===================== */}
      <footer className="lpFooter">
        <div className="lpContainer">
          <div className="footerTop">
            <div className="footerBrand">
              <div className="footerLogo">التنظيم للسفر والسياحة</div>
              <div className="footerDesc">
                برامج سياحية لوجهات متعددة — تخطيط احترافي، دعم عربي، وأسعار
                واضحة.
              </div>

              <div className="footerPills">
                <span className="footerPill">
                  <ShieldCheck size={16} /> جهة موثوقة
                </span>
                <span className="footerPill">
                  <Headset size={16} /> دعم عربي
                </span>
                <span className="footerPill">
                  <Timer size={16} /> استجابة سريعة
                </span>
              </div>
            </div>

            <div className="footerCols">
              <div className="footerCol">
                <div className="footerTitle">روابط سريعة</div>
                <button
                  type="button"
                  className="footerLink"
                  onClick={() => scrollTo(formRef)}
                >
                  إرسال الطلب
                </button>
                <button
                  type="button"
                  className="footerLink"
                  onClick={() => scrollTo(destinationsRef)}
                >
                  الوجهات
                </button>
                <button
                  type="button"
                  className="footerLink"
                  onClick={() => scrollTo(licensesRef)}
                >
                  الاعتمادات
                </button>
                <button
                  type="button"
                  className="footerLink"
                  onClick={() => scrollTo(faqRef)}
                >
                  الأسئلة الشائعة
                </button>
              </div>

              <div className="footerCol">
                <div className="footerTitle">تواصل</div>
                <div className="footerItem">واتساب: +966 55 916 3952</div>
                <div className="footerItem">الدعم: متاح باللغة العربية</div>

                <div className="footerActions">
                  <button
                    type="button"
                    className="footerBtn"
                    onClick={onWhatsApp}
                  >
                    واتساب <MessageCircle size={18} />
                  </button>
                  <button
                    type="button"
                    className="footerBtn isGhost"
                    onClick={() => scrollTo(formRef)}
                  >
                    إرسال طلب <ArrowLeft size={18} />
                  </button>
                </div>

                <div className="footerSmall">
                  خصوصية • اعتمادات • ملاحظات مهمة
                </div>
              </div>
            </div>
          </div>

          <div className="footerBottom">
            <span>© {new Date().getFullYear()} التنظيم للسفر والسياحة</span>
            <span className="footerDot">•</span>
            <span>Landing بدون تشتيت</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
